import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Gift, Star, Heart, X, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const KEY = "itz_support_prompt_day", OPENS = "itz_app_opens", HIDDEN_AT = "itz_hidden_at";
const today = () => new Date().toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" });
const PERKS = [
  [BadgeCheck, "Pinker Verifiziert-Haken an deinem Namen"],
  [Heart, "Liebevolles Herz-Profil in Kirschrot & Rosé-Gold"],
  [Sparkles, "Beiträge & Kommentare mit leuchtendem Kirschrot-Schimmer"],
  [Gift, "Exklusive Geschenke nur für Projekt Unterstützer"],
  [Star, "Rosé-Gold-Name überall in der Community"],
  [Heart, "Platz auf unserer Dankeschön-Wand"],
];

const countOpen = () => {
  const d = today();
  let o; try { o = JSON.parse(localStorage.getItem(OPENS)) || {}; } catch { o = {}; }
  const n = o.day === d ? o.n + 1 : 1;
  localStorage.setItem(OPENS, JSON.stringify({ day: d, n }));
  return n;
};

export function SupportPrompt() {
  const [open, setOpen] = useState(false);
  const [pushed, setPushed] = useState(false);
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; }, [open]);
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (!user || user.supporter) return;
    let t, busy = false;
    const tryOpen = (delay) => { if (busy || openRef.current) return; busy = true; t = setTimeout(function go() { if (document.querySelector('[role="dialog"],[data-testid="lucky-wheel-modal"],[data-testid="new-games-intro"],[data-testid="vip-celebration"]')) { t = setTimeout(go, 3000); return; } busy = false; openRef.current = true; setOpen(true); }, delay); };
    const check = (n) => { if (n >= 2 && localStorage.getItem(KEY) !== today()) tryOpen(6000); };
    const poll = () => !openRef.current && api.get("/support-prompt/pending").then(({ data }) => { if (data.pending) { setPushed(true); tryOpen(1500); } }).catch(() => {});
    check(countOpen());
    poll();
    const iv = setInterval(() => document.visibilityState === "visible" && poll(), 20000);
    const vis = () => {
      if (document.hidden) { localStorage.setItem(HIDDEN_AT, String(Date.now())); return; }
      poll();
      if (Date.now() - Number(localStorage.getItem(HIDDEN_AT) || Date.now()) > 10 * 60 * 1000) check(countOpen());
    };
    document.addEventListener("visibilitychange", vis);
    return () => { clearTimeout(t); clearInterval(iv); document.removeEventListener("visibilitychange", vis); };
  }, [user?.user_id, user?.supporter]); // eslint-disable-line react-hooks/exhaustive-deps
  const answer = (more) => {
    localStorage.setItem(KEY, today());
    if (pushed) { api.post("/support-prompt/seen", { action: more ? "more" : "no" }).catch(() => {}); setPushed(false); }
    setOpen(false);
    if (more) navigate("/community-unterstuetzen");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && answer(false)}>
      <DialogContent className="vip-lux vip-prompt-in rounded-[2rem] max-w-sm p-0 max-h-[92vh] overflow-y-auto border-0 [&>button]:hidden" data-testid="support-prompt">
        <span className="vip-lux-pattern" aria-hidden />
        <div className="relative px-6 pt-8 pb-6 text-center">
          <button onClick={() => answer(false)} aria-label="Schließen" data-testid="support-prompt-close" className="absolute top-3 right-3 p-1.5 rounded-full text-amber-200/70 hover:text-amber-100 hover:bg-white/10"><X className="w-4 h-4" /></button>
          {[0, 1, 2, 3, 4].map((i) => <Sparkles key={i} className="vip-prompt-spark absolute w-4 h-4 text-amber-300" style={{ left: `${[10, 86, 22, 74, 50][i]}%`, top: `${[5, 6, 13, 12, 2][i]}%`, animationDelay: `${i * 0.4}s` }} />)}
          <span className="vip-prompt-crown relative mx-auto w-20 h-20 rounded-full vip-gold-metal flex items-center justify-center shadow-[0_0_40px_rgba(251,191,36,0.55)]">
            <Heart className="w-10 h-10" fill="currentColor" />
          </span>
          <p className="mt-4 text-[11px] font-extrabold tracking-[0.3em] text-amber-300/90">PREMIUM · PROJEKT UNTERSTÜTZER</p>
          <DialogTitle className="mt-1 font-display text-2xl font-extrabold vip-gold-title">Möchtest du Projekt Unterstützer werden?</DialogTitle>
          <DialogDescription className="mt-2 text-sm text-amber-50/80">Du hilfst uns, die hohen Kosten der Community zu tragen – und bekommst als Dankeschön:</DialogDescription>
          <ul className="mt-4 space-y-2 text-left" data-testid="support-prompt-perks">
            {PERKS.map(([Icon, t], i) => (
              <li key={t} className="vip-prompt-perk flex items-center gap-3 rounded-2xl bg-white/5 border border-amber-300/20 px-3 py-2" style={{ animationDelay: `${0.25 + i * 0.08}s` }}>
                <span className="shrink-0 w-7 h-7 rounded-full vip-gold-metal flex items-center justify-center"><Icon className="w-3.5 h-3.5" /></span>
                <span className="text-sm font-semibold text-amber-50">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 grid grid-cols-[1fr_1.4fr] gap-3">
            <button onClick={() => answer(false)} data-testid="support-prompt-no" className="h-12 rounded-full border border-amber-300/40 text-amber-100 font-semibold hover:bg-white/10 transition-colors">Nein</button>
            <button onClick={() => answer(true)} data-testid="support-prompt-more" className="h-12 rounded-full vip-gold-metal font-extrabold shadow-lg shadow-amber-500/30 active:scale-95 transition-transform">Mehr Informationen</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
